import React from "react";

class LocalizationManager {
  constructor() {
    const savedLang = localStorage.getItem("userLang");
    const supportedLangs = ["en", "zh-TW", "ja"];

    // Browser detection
    const browserLang = navigator.language.toLowerCase();
    const shortLang = browserLang.split("-")[0];

    // Normalize saved lang (handle legacy 'zh' and 'zh-tw')
    let effectiveSaved = savedLang;
    if (savedLang === "zh" || savedLang === "zh-tw") effectiveSaved = "zh-TW";

    if (effectiveSaved && supportedLangs.includes(effectiveSaved)) {
      this.currentLang = effectiveSaved;
    } else {
      // Logic for first visit detection fallback
      if (shortLang === "ja") {
          this.currentLang = "ja";
      } else if (shortLang === "zh" || browserLang === "zh-TW" || browserLang === "zh-tw" || browserLang === "zh-cn") {
          this.currentLang = "zh-TW";
      } else {
          this.currentLang = "en";
      }
      localStorage.setItem("userLang", this.currentLang);
    }

    this.stringMap = new Map();
    this.isLoaded = false;
    this.loadingPromise = null;
  }

  async setupLocalization(lang = this.currentLang) {
    this.currentLang = lang;

    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = (async () => {
      try {
        const res = await fetch("/data/localization.json");
        const localData = await res.json();

        this.stringMap.clear();
        localData.strings.forEach((item) => {
          // Map zh-TW to zh for JSON access (localization.json still uses 'zh' key)
          const jsonKey = this.currentLang === "zh-TW" ? "zh" : this.currentLang;
          this.stringMap.set(item.key, item[jsonKey]);
        });

        localStorage.setItem("userLang", this.currentLang);
        this.isLoaded = true;

        document.documentElement.lang = this.currentLang;
        this.applyFontByLanguage(this.currentLang);
      } catch (err) {
        console.error("Failed to load localization.json", err);
      } finally {
        this.loadingPromise = null;
      }
    })();

    return this.loadingPromise;
  }

  applyFontByLanguage(lang) {
    let fontFamily = "";

    switch (lang) {
      case "ja":
        fontFamily = `"Noto Sans JP", "Hiragino Sans", "Meiryo", sans-serif`;
        break;
      case "zh":
      case "zh-tw":
      case "zh-TW":
        fontFamily = `"Noto Sans TC", "PingFang TC", "Microsoft JhengHei", sans-serif`;
        break;
      default:
        fontFamily = `"Roboto", "Helvetica", "Arial", sans-serif`;
    }

    const existingStyle = document.getElementById("lang-font-style");
    if (existingStyle) existingStyle.remove();

    const style = document.createElement("style");
    style.id = "lang-font-style";
    style.innerHTML = `
      html, body, * {
        font-family: ${fontFamily} !important;
      }
    `;
    document.head.appendChild(style);
  }

  getLocalStringPlain(key, variables = {}) {
    const has = this.stringMap?.has?.(key);
    if (!has && process.env.NODE_ENV !== "production") {
      console.warn(`[i18n] Missing key: ${key}`);
    }
    let raw = has ? this.stringMap.get(key) : key;
    raw = String(raw ?? key);

    // Recursive resolution of {@@key} patterns
    let match;
    const braceRegex = /\{@@([^}]+)\}/g;
    while ((match = braceRegex.exec(raw)) !== null) {
      const fullMatch = match[0];
      // Resolve the inner key
      const val = this.getLocalStringPlain("@@" + match[1]);
      raw = raw.replace(fullMatch, val);
      braceRegex.lastIndex = 0;
    }

    for (const varKey of Object.keys(variables)) {
      const re = new RegExp(`@@${varKey}@@`, "g");
      raw = raw.replace(re, String(variables[varKey] ?? ""));
    }
    return raw;
  }

  getLocalString(key, variables = {}) {
    // 1) fetch & fallback to the key itself
    const has = this.stringMap?.has?.(key);
    // Dev-only warning for missing translations
    if (!has && process.env.NODE_ENV !== "production") {
      console.warn(`[i18n] Missing key: ${key}`);
    }
    let raw = has ? this.stringMap.get(key) : key;

    // 2) force to string to avoid `.split` on non-strings (numbers, null, etc.)
    raw = String(raw ?? key);

    // 3A) Recursive resolution of {key} patterns
    // We do this loop to handle nested keys if necessary, or just simple {@@key@@} replacement
    // The user requirement changed from "prefix|key" to "prefix {@@key} suffix"
    // So we look for {@@...} pattern and replace it with getLocalStringPlain of that key.
    // Note: The user request examples use {@@product_des_sn_bt}, so we match {@@...}
    let match;
    const braceRegex = /\{@@([^}]+)\}/g;
    while ((match = braceRegex.exec(raw)) !== null) {
      const fullMatch = match[0];
      //const innerKey = "@@" + match[1]; // The regex captures inside {@@...}, so we add @@ back or adjust regex
      // Actually, if the string is "1000W {@@product_des_sn_bt}", the regex \{@@([^}]+)\} 
      // captures "product_des_sn_bt" in group 1.
      // We want to fetch the translation for "@@product_des_sn_bt".
      const val = this.getLocalStringPlain("@@" + match[1]);
      raw = raw.replace(fullMatch, val);
      // Reset regex index because string changed
      braceRegex.lastIndex = 0;
    }

    // 3) interpolate @@vars@@ (also coerced to strings)
    for (const varKey of Object.keys(variables)) {
      const re = new RegExp(`@@${varKey}@@`, "g");
      raw = raw.replace(re, String(variables[varKey] ?? ""));
    }

    // 4) your newline style: split on literal "/n" or a real newline
    const lines = raw.split(/\/n|\r?\n/);

    return lines.map((line, i) => (
      <React.Fragment key={i}>
        {String(line)
          .split(/(\*\*.*?\*\*|\*.*?\*|__.*?__)/g)
          .map((seg, j) => {
            if (
              typeof seg === "string" &&
              seg.startsWith("**") &&
              seg.endsWith("**")
            ) {
              return <b key={j}>{seg.slice(2, -2)}</b>;
            } else if (
              typeof seg === "string" &&
              seg.startsWith("*") &&
              seg.endsWith("*")
            ) {
              return <i key={j}>{seg.slice(1, -1)}</i>;
            } else if (
              typeof seg === "string" &&
              seg.startsWith("__") &&
              seg.endsWith("__")
            ) {
              return <u key={j}>{seg.slice(2, -2)}</u>;
            } else {
              return seg;
            }
          })}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  }
}

const localizationManager = new LocalizationManager();
export default localizationManager;
